import { StyledTable } from '@/shared/ui/tables'
import React, { type JSX } from 'react'
import {
    Alert,
    Box,
    Chip,
    FormControl,
    FormLabel,
    Grid,
    Paper,
    Tab,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material'
import Loading from '@/shared/composed-ui/LoadingData'
import Pagination from '@/shared/ui/pagination/Pagination'
import ItemCounter from '@/shared/composed-ui/ItemCounter'
import SelectRC, { Option } from 'rc-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown'
import AppDialog from '@/shared/ui/app-dialog/AppDialog'
import { StyledButton } from '@/shared/ui/buttons'

interface User {
    id: number
    username: string
    profile: {
        first_name: string
        last_name: string
    }
}

interface LogItem {
    impersonate_user: number
    id: number
    type: 'shown' | 'change' | 'archive' | 'delete' | 'secureshare'
    changes: string
    datetime: string
    user_id: number
    username?: string
}

interface MailItem {
    id: number
    subject: string
    recipient: string
    status: 'sent' | 'delivered' | 'failed' | string
    created_at: string
    delivered_at: string
}

interface Pager {
    current_page: number
    total_pages: number
    total_items: number
}

interface SelectOption {
    value: string | number
    label: string
}

interface Dossier {
    id: number
    summary: string
}

interface KedoAPI {
    get: (endpoint: string, params: any) => Promise<any>
    getDossierLogEndpoint: () => string
    getMailEndpoint: () => string
    getUserEndpoint: () => string
}

interface KedoEnv {
    getEnvironment: () => { id: number }
    hasEnviroment: () => boolean
}

interface KedoUtils {
    dateFormat: (date: string) => string
}

interface Kedo {
    api: () => KedoAPI
    env: () => KedoEnv
    utils: () => KedoUtils
    t: (key: string, options?: any) => string
}

interface ContentDossierLogProps {
    kedo: Kedo
    dossier: Dossier
    onClose: () => void
}

interface ContentDossierLogState {
    logs: LogItem[]
    mails: MailItem[]
    searchType: string | null
    searchUser: number | null
    page: number
    pager: Pager | Record<string, never>
    mailPager: Pager | Record<string, never>
    loading: boolean
    mailLoading: boolean
    users?: User[]
    loadUsers?: boolean
    error?: string
    selectedUser?: SelectOption | null
    activeTab: number
}

class ContentDossierLog extends React.Component<
    ContentDossierLogProps,
    ContentDossierLogState
> {
    constructor(props: ContentDossierLogProps) {
        super(props)

        this.state = {
            logs: [],
            mails: [],
            searchType: null,
            searchUser: null,
            page: 0,
            pager: {},
            mailPager: {},
            loading: false,
            mailLoading: false,
            activeTab: 0,
        }

        this.fetchData = this.fetchData.bind(this)
        this.fetchMaillogData = this.fetchMaillogData.bind(this)
        this.getUserSearch = this.getUserSearch.bind(this)
        this.getTypeSearch = this.getTypeSearch.bind(this)
        this.onChangePage = this.onChangePage.bind(this)
        this.setSearch = this.setSearch.bind(this)
    }

    onChangePage(number: number): void {
        this.setState({ page: number }, this.fetchData)
    }

    fetchMaillogData(): void {
        const api = this.props.kedo.api()
        const params = {
            params: {
                environment: this.props.kedo.env().getEnvironment().id,
                page: this.state.page,
                dossier: this.props.dossier.id,
            },
        }

        if (!this.props.kedo.env().hasEnviroment()) {
            this.setState({
                error: this.props.kedo.t('No environment selected'),
            })
            return
        }

        this.setState({ mailLoading: true })
        api.get(api.getMailEndpoint(), params)
            .then((response) =>
                this.setState({
                    mails: response.data.results,
                    mailPager: response.data.pager,
                    mailLoading: false,
                })
            )
            .catch(() => this.setState({ mailLoading: false }))
    }

    fetchData(): void {
        const api = this.props.kedo.api()
        const params: any = {
            params: {
                environment: this.props.kedo.env().getEnvironment().id,
                page: this.state.page,
                dossier: this.props.dossier.id,
            },
        }

        if (!this.props.kedo.env().hasEnviroment()) {
            this.setState({
                error: this.props.kedo.t('No environment selected'),
            })
            return
        }

        if (this.state.searchUser !== null) {
            params.params.user = this.state.searchUser
        }

        if (
            this.state.searchType !== null &&
            this.state.searchType.length > 0
        ) {
            params.params.type = this.state.searchType
        }

        this.setState({ loading: true })
        api.get(api.getDossierLogEndpoint(), params).then((response) =>
            this.setState({
                logs: response.data.results,
                pager: response.data.pager,
                loading: false,
            })
        )
    }

    getChangedData(item: LogItem) {
        let changes: any = JSON.parse(item.changes)
        const kedo = this.props.kedo

        if (item.type === 'change' && changes.length > 0) {
            return (
                <ul>
                    {changes.map((changeItem: any, changeIndex: number) => (
                        <li key={changeIndex}>
                            <strong>{changeItem.defDisplayItemName}</strong>
                            <br />
                            <s>{changeItem.oldValue}</s> <br />
                            {changeItem.newValue} <br />
                        </li>
                    ))}
                </ul>
            )
        }

        if (item.type === 'archive' && changes.length > 0) {
            return (
                <ul>
                    {changes.map((changeItem: any, changeIndex: number) => (
                        <li key={changeIndex}>{changeItem.newValue}</li>
                    ))}
                </ul>
            )
        }

        if (item.type === 'delete' && changes.length > 0) {
            return (
                <Box component="strong" sx={{ color: 'error.main' }}>
                    {this.props.kedo.t('Deleted')}
                </Box>
            )
        }

        if (item.type === 'secureshare') {
            changes = changes[0]
            return (
                <ul>
                    {changes?.extra_data?.shareType ? (
                        <li>
                            <strong>{kedo.t('Share type')}</strong>{' '}
                            {changes.extra_data.shareType}
                        </li>
                    ) : null}
                    {changes?.extra_data?.shareValue ? (
                        <li>
                            <strong>{kedo.t('Value')}</strong>{' '}
                            {changes.extra_data.shareValue}
                        </li>
                    ) : null}
                    {changes?.extra_data?.contacts &&
                    changes.extra_data.contacts.length > 0
                        ? changes.extra_data.contacts.map(
                              (contactItem: any) => (
                                  <li key={`${contactItem.email}`}>
                                      {contactItem.email}
                                  </li>
                              )
                          )
                        : null}
                </ul>
            )
        }

        return (
            <Box component="small" sx={{ color: 'text.disabled' }}>
                {this.props.kedo.t('No changes')}
            </Box>
        )
    }

    fetchUsers(): void {
        const params = {
            params: {
                environment: this.props.kedo.env().getEnvironment().id,
            },
        }

        this.setState({ loadUsers: true })
        this.props.kedo
            .api()
            .get(this.props.kedo.api().getUserEndpoint(), params)
            .then((response) => {
                this.setState({
                    users: response.data.results,
                    loadUsers: false,
                })
            })
    }

    getUserSearch(): JSX.Element {
        let selectOptions: SelectOption[] = []

        if (this.state.users && this.state.users.length > 0) {
            selectOptions = this.state.users.map((item) => ({
                value: item.id,
                label: `${item.username} (${item.profile.first_name} ${item.profile.last_name})`,
            }))
        }

        return (
            <SelectRC
                className="w-100 custom-select-react"
                name={'Select User Dossierlog'}
                allowClear
                showSearch
                placeholder={this.props.kedo.t('Search') + '...'}
                value={
                    this.state.selectedUser
                        ? this.state.selectedUser.value
                        : undefined
                }
                onChange={(selectedItem: any) => {
                    const selectedUser = selectOptions.find(
                        (option) => option.value === selectedItem
                    )
                    this.setSearch('searchUser', selectedUser || null)
                }}
                filterOption={(input: string, option: any) =>
                    option.props.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                }
                suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
                notFoundContent={this.props.kedo.t('No users found')}
            >
                {selectOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </SelectRC>
        )
    }

    getTypeSearch() {
        const selectOptions: SelectOption[] = [
            { value: 'shown', label: this.props.kedo.t('log_types.shown') },
            { value: 'change', label: this.props.kedo.t('log_types.change') },
            { value: 'archive', label: this.props.kedo.t('log_types.archive') },
            { value: 'delete', label: this.props.kedo.t('log_types.delete') },
            {
                value: 'secureshare',
                label: this.props.kedo.t('log_types_secureshare', {
                    ns: 'secureshare',
                }),
            },
        ]

        // Single type only: API params.type is one string. mode="multiple" was
        // UI-only and onChange kept [0]. To support multi, change onChange,
        // searchType state, and fetchData.
        return (
            <SelectRC
                className="w-100 custom-select-react"
                name={'Select Type Dossierlog'}
                allowClear
                showSearch
                placeholder={this.props.kedo.t('Search') + '...'}
                value={
                    this.state.searchType !== null
                        ? this.state.searchType
                        : undefined
                }
                onChange={(selectedItem: any) => {
                    const selectedObject =
                        selectOptions.find(
                            (option) => option.value === selectedItem
                        ) || null
                    this.setSearch('searchType', selectedObject)
                }}
                filterOption={(input: string, option: any) =>
                    String(option.props.children)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                }
                suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
                notFoundContent={this.props.kedo.t('No items found')}
            >
                {selectOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </SelectRC>
        )
    }

    setSearch(type: string, item: SelectOption | null): void {
        this.setState(
            { [type]: item ? item.value : null } as any,
            this.fetchData
        )
    }

    translateType(type: string): string {
        if (type === 'archive') {
            return this.props.kedo.t('log_types.archive')
        }
        if (type === 'change') {
            return this.props.kedo.t('log_types.change')
        }
        if (type === 'delete') {
            return this.props.kedo.t('log_types.delete')
        }
        if (type === 'shown') {
            return this.props.kedo.t('log_types.shown')
        }
        if (type === 'secureshare') {
            return this.props.kedo.t('log_types_secureshare', {
                ns: 'secureshare',
            })
        }
        return type
    }

    getMailStatusClass(
        status: string
    ): 'info' | 'success' | 'error' | 'default' {
        if (status === 'sent') {
            return 'info'
        } else if (status === 'delivered') {
            return 'success'
        } else if (status === 'failed') {
            return 'error'
        } else {
            return 'default'
        }
    }

    componentDidMount(): void {
        this.fetchData()
        this.fetchUsers()
    }

    handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
        this.setState({ activeTab: newValue })
        if (newValue === 1) {
            this.fetchMaillogData()
        }
    }

    render() {
        const kedo = this.props.kedo
        const { activeTab } = this.state

        return (
            <AppDialog
                aria-labelledby="contained-modal-title-vcenter"
                open
                maxWidth={'lg'}
                fullWidth
                topAlign
                title={
                    <>
                        {kedo.t('Logs')}: {this.props.dossier.summary}
                    </>
                }
                onClose={() => this.props.onClose()}
                headerClose={() => this.props.onClose()}
                contentProps={{ dividers: true }}
                actions={
                    <StyledButton
                        onClick={() => this.props.onClose()}
                        color="secondary"
                        title={kedo.t('Close')}
                    >
                        {kedo.t('Close')}
                    </StyledButton>
                }
            >
                <Box>
                    <Tabs
                        value={activeTab}
                        onChange={this.handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label={kedo.t('Logs')} />
                        <Tab label={kedo.t('Mail')} />
                    </Tabs>

                    {/* Dossier Log Tab */}
                    {activeTab === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <h5>{kedo.t('Search')}</h5>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <FormLabel>{kedo.t('Type')}</FormLabel>
                                        {this.getTypeSearch()}
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <FormLabel>{kedo.t('Users')}</FormLabel>
                                        {this.getUserSearch()}
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <TableContainer component={Paper}>
                                <StyledTable size="small" striped>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                mt: 2,
                                                '& .MuiTableCell-head': {
                                                    textAlign: 'left',
                                                    fontWeight: 'bold',
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                {kedo.t('Date')}
                                            </TableCell>
                                            <TableCell>
                                                {kedo.t('Type')}
                                            </TableCell>
                                            <TableCell>
                                                {kedo.t('User')}
                                            </TableCell>
                                            <TableCell>
                                                {kedo.t('Changes')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody sx={{}}>
                                        {this.state.loading ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Loading />
                                                </TableCell>
                                            </TableRow>
                                        ) : this.state.logs &&
                                          this.state.logs.length > 0 ? (
                                            this.state.logs.map((item) => {
                                                return (
                                                    <TableRow key={item.id}>
                                                        <TableCell>
                                                            {kedo
                                                                .utils()
                                                                .dateFormat(
                                                                    item.datetime
                                                                )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {this.translateType(
                                                                item.type
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <a
                                                                href={`/user/${item.user_id}`}
                                                            >
                                                                {item.username
                                                                    ? item.username
                                                                    : item.user_id}
                                                            </a>

                                                            {item.impersonate_user && (
                                                                <Typography
                                                                    variant="caption"
                                                                    display="block"
                                                                    sx={{
                                                                        fontFamily:
                                                                            'monospace',
                                                                        opacity: 0.7,
                                                                    }}
                                                                >
                                                                    impersonated
                                                                    by{' '}
                                                                    <a
                                                                        href={`/user/${item.impersonate_user}`}
                                                                    >
                                                                        {
                                                                            item.impersonate_user
                                                                        }
                                                                    </a>
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {this.getChangedData(
                                                                item
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Alert
                                                        severity="info"
                                                        variant="outlined"
                                                    >
                                                        &nbsp;{' '}
                                                        {kedo.t(
                                                            'No items found'
                                                        )}
                                                    </Alert>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Pagination
                                                    pager={this.state.pager}
                                                    onChangePage={
                                                        this.onChangePage
                                                    }
                                                />
                                                <ItemCounter
                                                    pager={this.state.pager}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </StyledTable>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Mail Log Tab */}
                    {activeTab === 1 && (
                        <Box sx={{ mt: 2 }}>
                            {this.state.mailLoading ? <Loading /> : null}
                            {!this.state.mailLoading ? (
                                <TableContainer component={Paper}>
                                    <StyledTable size="small" striped>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    {kedo.t('Id')}
                                                </TableCell>
                                                <TableCell>
                                                    {kedo.t('Subject')}
                                                </TableCell>
                                                <TableCell>
                                                    {kedo.t('Recipient')}
                                                </TableCell>
                                                <TableCell>
                                                    {kedo.t('Status')}
                                                </TableCell>
                                                <TableCell>
                                                    {kedo.t('Created')}
                                                </TableCell>
                                                <TableCell>
                                                    {kedo.t('Delivered')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody sx={{}}>
                                            {this.state.mails.map((item) => {
                                                return (
                                                    <TableRow key={item.id}>
                                                        <TableCell>
                                                            {item.id}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.subject}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.recipient}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={
                                                                    item.status
                                                                }
                                                                color={this.getMailStatusClass(
                                                                    item.status
                                                                )}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {kedo
                                                                .utils()
                                                                .dateFormat(
                                                                    item.created_at
                                                                )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {kedo
                                                                .utils()
                                                                .dateFormat(
                                                                    item.delivered_at
                                                                )}
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </StyledTable>
                                </TableContainer>
                            ) : null}
                        </Box>
                    )}
                </Box>
            </AppDialog>
        )
    }
}

export default ContentDossierLog
